import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import {
  useGetMyPEARReviewsQuery,
  useGetAllPEARReviewsQuery,
  useDeletePEARReviewMutation,
  PEARReview,
} from '../../redux/pearReviewApi';
// toast removed - react-toastify v8 incompatible with React 19
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Status chip colors
const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  submitted: 'info',
  under_review: 'warning',
  approved: 'success',
  rejected: 'error',
};

// Grade chip colors
const gradeColors: Record<string, string> = {
  A: '#4caf50',
  B: '#8bc34a',
  C: '#ff9800',
  D: '#f44336',
};

const PEARReviewDashboard: React.FC = () => {
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.roles?.includes('admin');
  const isManager = user?.roles?.includes('manager');

  // Queries
  const {
    data: myReviews,
    isLoading: isLoadingMy,
    refetch: refetchMy,
  } = useGetMyPEARReviewsQuery();

  const {
    data: allReviews,
    isLoading: isLoadingAll,
    refetch: refetchAll,
  } = useGetAllPEARReviewsQuery(undefined, {
    skip: !isAdmin && !isManager,
  });

  const [deletePEAR] = useDeletePEARReviewMutation();

  // State
  const [viewMode, setViewMode] = useState<'my' | 'all'>(isAdmin || isManager ? 'all' : 'my');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Get reviews based on view mode
  const reviews = viewMode === 'my' ? myReviews : allReviews;
  const isLoading = viewMode === 'my' ? isLoadingMy : isLoadingAll;

  // Filter reviews
  const filteredReviews = reviews?.filter((review) => {
    if (statusFilter === 'all') return true;
    return review.status === statusFilter;
  });

  // Handlers
  const handleCreateNew = () => {
    router.push('/reviews/pear');
  };

  const handleEdit = (reviewPeriod: string) => {
    router.push(`/reviews/pear?period=${encodeURIComponent(reviewPeriod)}`);
  };

  const handleView = (reviewPeriod: string) => {
    router.push(`/reviews/pear?period=${encodeURIComponent(reviewPeriod)}`);
  };

  const handleDelete = (id: string, employeeName?: string) => {
    confirmAlert({
      title: 'Delete PEAR Review',
      message: `Are you sure you want to delete the PEAR Review for ${employeeName || 'this employee'}?`,
      buttons: [
        {
          label: 'Yes, Delete',
          onClick: async () => {
            try {
              await deletePEAR({ id }).unwrap();
              refetchMy();
              if (isAdmin || isManager) refetchAll();
            } catch (error) {
              console.error('Failed to delete PEAR Review:', error);
            }
          },
        },
        {
          label: 'Cancel',
          onClick: () => {},
        },
      ],
    });
  };

  const handleRefresh = () => {
    refetchMy();
    if (isAdmin || isManager) refetchAll();
  };

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Back Button */}
        <Button
          color="primary"
          onClick={() => router.push('/reviews')}
          sx={{ mb: 2 }}
          startIcon={<ArrowBackIcon />}
        >
          Back to Reviews
        </Button>

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              PEAR Reviews
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quarterly Performance Evaluation Assessment Reviews
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              New PEAR Review
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {(isAdmin || isManager) && (
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewMode}
                  label="View"
                  onChange={(e: SelectChangeEvent) =>
                    setViewMode(e.target.value as 'my' | 'all')
                  }
                >
                  <MenuItem value="my">My Reviews</MenuItem>
                  <MenuItem value="all">All Reviews</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold">
                  {reviews?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Reviews
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {reviews?.filter((r) => r.status === 'approved').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {reviews?.filter((r) => r.status === 'submitted').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="text.secondary">
                  {reviews?.filter((r) => r.status === 'draft').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drafts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Reviews Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredReviews && filteredReviews.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Review Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Grade</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReviews.map((review) => (
                  <TableRow key={review.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {review.employeeName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {review.designation || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {review.reviewPeriod || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={review.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                        size="small"
                        color={statusColors[review.status || 'draft']}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {review.overallScore?.toFixed(1) || '-'}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {review.finalGrade && (
                        <Chip
                          label={review.finalGrade}
                          size="small"
                          sx={{
                            bgcolor: gradeColors[review.finalGrade] || '#9e9e9e',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {review.createdAt
                          ? new Date(review.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleView(review.reviewPeriod)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {review.status === 'draft' && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(review.reviewPeriod)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDelete(review.id, review.employeeName)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No PEAR Reviews found
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{ mt: 2 }}
              >
                Create Your First PEAR Review
              </Button>
            </Box>
          )}
        </TableContainer>
      </Box>
    </PageWithHeader>
  );
};

export default PEARReviewDashboard;
